import { supabase } from './supabase';

interface UpdateOptions {
  table: string;
  operation: 'insert' | 'update' | 'delete' | 'bulk-update' | 'update-by-auth-id' | 'toggle-active';
  data?: any;
  id?: string;
  authUserId?: string;
  filters?: Record<string, any>;
}

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No session found');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
  };
}

export async function adminUpdate(options: UpdateOptions) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No session found');
  }

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-update-data`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to ${options.operation}: ${errorText}`);
  }

  const result = await response.json();
  return result;
}

/**
 * Volledige gebruiker-verwijdering via de server-side `delete-user` edge-function
 * (service_role). Ruimt public.users (cascade + storage), auth.users + identities,
 * Upload-Post-profiel en social_profiles op, en blokkeert een gekoppeld WhatsApp-nummer.
 * Vervangt de oude client-side delete die het auth-account liet staan.
 */
export async function deleteUser(userId: string): Promise<{ success: boolean; auth_deleted?: boolean; warnings?: string[] }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No session found');
  }

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id: userId }),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    throw new Error(body?.error || body?.detail || `Failed to delete user: ${response.status}`);
  }
  return body;
}

/**
 * Registreert een opzegging namens een klant vanuit het admin dashboard.
 * Schrijft naar dezelfde tabel als de self-service opzegflow, zodat de opzegging
 * via de bestaande hub-sync automatisch in hub.dibbi.nl verschijnt.
 */
export async function registerCancellation(input: {
  user_id: string;
  reasons: string[];
  reason_other?: string;
  effective_end_date?: string;
}): Promise<{
  success: boolean;
  cancellation_id: string;
  effective_end_date: string;
}> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No session found');
  }

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-cancel-subscription`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    throw new Error(body?.error || `Opzegging registreren mislukt: ${response.status}`);
  }
  return body;
}

export const adminUsersApi = {
  list: async (params: {
    page: number;
    per_page: number;
    sort_by: string;
    sort_order: string;
    filter_dns?: boolean;
    filter_widget?: boolean;
    filter_planning?: boolean;
    filter_user_type?: 'all' | 'regular' | 'admin' | 'support';
    search?: string;
  }) => {
    const queryParams = new URLSearchParams({
      action: 'list',
      page: params.page.toString(),
      per_page: params.per_page.toString(),
      sort_by: params.sort_by,
      sort_order: params.sort_order,
    });

    if (params.filter_dns) queryParams.append('filter_dns', 'true');
    if (params.filter_widget) queryParams.append('filter_widget', 'true');
    if (params.filter_planning) queryParams.append('filter_planning', 'true');
    if (params.filter_user_type) queryParams.append('filter_user_type', params.filter_user_type);
    if (params.search) queryParams.append('search', params.search);

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users?${queryParams}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load users: ${await response.text()}`);
    }

    return await response.json();
  },

  detail: async (userId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users?action=detail&userId=${userId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load user detail: ${await response.text()}`);
    }

    return await response.json();
  },

  all: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users?action=all`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load all users: ${await response.text()}`);
    }

    return await response.json();
  },
};

export const adminFinancialApi = {
  costStats: async (period: string = 'today') => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-financial?action=cost-stats&period=${period}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load cost stats: ${await response.text()}`);
    }

    return await response.json();
  },

  userCosts: async (period: string = 'today') => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-financial?action=user-costs&period=${period}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load user costs: ${await response.text()}`);
    }

    return await response.json();
  },

  userCredits: async (userId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-financial?action=user-credits&userId=${userId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load user credits: ${await response.text()}`);
    }

    return await response.json();
  },

  userCostSummary: async (userId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-financial?action=user-cost-summary&userId=${userId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load user cost summary: ${await response.text()}`);
    }

    return await response.json();
  },

  userFinancialOverview: async (userId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-financial?action=user-financial-overview&userId=${userId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load user financial overview: ${await response.text()}`);
    }

    return await response.json();
  },
};

export const adminMonitoringApi = {
  dashboardStats: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-monitoring?action=dashboard-stats`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load dashboard stats: ${await response.text()}`);
    }

    return await response.json();
  },

  systemHealthStatus: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-monitoring?action=system-health-status`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load system health status: ${await response.text()}`);
    }

    return await response.json();
  },

  recentSystemErrors: async (params: { limit?: number; offset?: number; include_reviewed?: boolean }) => {
    const queryParams = new URLSearchParams({
      action: 'recent-system-errors',
      limit: (params.limit || 50).toString(),
      offset: (params.offset || 0).toString(),
    });

    if (params.include_reviewed) {
      queryParams.append('include_reviewed', 'true');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-monitoring?${queryParams}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load system errors: ${await response.text()}`);
    }

    return await response.json();
  },

  promptLogs: async (params: {
    page: number;
    pageSize: number;
    filterType?: string;
    filterSuccess?: string;
    filterUser?: string;
  }) => {
    const queryParams = new URLSearchParams({
      type: 'prompt-logs',
      page: params.page.toString(),
      pageSize: params.pageSize.toString(),
    });

    if (params.filterType) queryParams.append('filterType', params.filterType);
    if (params.filterSuccess) queryParams.append('filterSuccess', params.filterSuccess);
    if (params.filterUser) queryParams.append('filterUser', params.filterUser);

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?${queryParams}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load prompt logs: ${await response.text()}`);
    }

    return await response.json();
  },

  storageLogs: async (params: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams({
      action: 'storage-logs',
      limit: (params.limit || 50).toString(),
      offset: (params.offset || 0).toString(),
    });

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-monitoring?${queryParams}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load storage logs: ${await response.text()}`);
    }

    return await response.json();
  },
};

export const adminConfigApi = {
  systemConfig: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-config?action=system-config`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load system config: ${await response.text()}`);
    }

    return await response.json();
  },

  emailTemplates: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-config?action=email-templates`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load email templates: ${await response.text()}`);
    }

    return await response.json();
  },

  aiModels: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-config?action=ai-models`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load AI models: ${await response.text()}`);
    }

    return await response.json();
  },

  promptTemplates: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-config?action=prompt-templates`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load prompt templates: ${await response.text()}`);
    }

    return await response.json();
  },
};

export const adminBlogApi = {
  blogInfo: async (blogId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-blog?action=blog-info&blogId=${blogId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load blog info: ${await response.text()}`);
    }

    return await response.json();
  },

  blogDetailCosts: async (blogId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-blog?action=blog-detail-costs&blogId=${blogId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load blog detail costs: ${await response.text()}`);
    }

    return await response.json();
  },

  userBlogCosts: async (userId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-blog?action=user-blog-costs&userId=${userId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load user blog costs: ${await response.text()}`);
    }

    return await response.json();
  },

  userUnpublishedSessions: async (userId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-blog?action=user-unpublished-sessions&userId=${userId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load user unpublished sessions: ${await response.text()}`);
    }

    return await response.json();
  },

  userEmailCampaignCosts: async (userId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-blog?action=user-email-campaign-costs&userId=${userId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load user email campaign costs: ${await response.text()}`);
    }

    return await response.json();
  },
};

export const adminEmailApi = {
  lookup: async (email: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-email-lookup?email=${encodeURIComponent(email)}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to lookup email: ${await response.text()}`);
    }

    return await response.json();
  },

  searchSendGridActivity: async (email: string, limit: number = 25) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sendgrid-search-activity?email=${encodeURIComponent(email)}&limit=${limit}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Failed to search SendGrid activity`);
    }

    return await response.json();
  },

  bulkReview: async (params: {
    action: 'mark_visible' | 'mark_by_campaign' | 'mark_by_timeframe';
    error_ids?: string[];
    campaign_id?: string;
    hours?: number;
    reviewed: boolean;
  }) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-bulk-review-emails`,
      {
        method: 'POST',
        headers: {
          ...await getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to bulk review emails: ${await response.text()}`);
    }

    return await response.json();
  },
};

export const adminExportApi = {
  exportUserData: async (userId: string): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No session found');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-user-data?userId=${userId}`,
      {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export mislukt: ${errorText}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
    const filename = filenameMatch ? filenameMatch[1] : 'dibbi-export.zip';

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

export const adminKnowledgeApi = {
  list: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=knowledge-list`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load knowledge items: ${await response.text()}`);
    }

    return await response.json();
  },

  item: async (itemId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=knowledge-item&itemId=${itemId}`,
      { headers: await getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to load knowledge item: ${await response.text()}`);
    }

    return await response.json();
  },

  create: async (data: {
    title: string;
    content: string;
    category?: string | null;
    tags?: string[] | null;
  }) => {
    return await adminUpdate({
      table: 'chat_knowledge_base',
      operation: 'insert',
      data: {
        ...data,
        source_type: 'manual',
        is_active: true,
      },
    });
  },

  update: async (id: string, data: {
    title?: string;
    content?: string;
    category?: string | null;
    tags?: string[] | null;
  }) => {
    return await adminUpdate({
      table: 'chat_knowledge_base',
      operation: 'update',
      id,
      data: {
        ...data,
        updated_at: new Date().toISOString(),
      },
    });
  },

  delete: async (id: string) => {
    return await adminUpdate({
      table: 'chat_knowledge_base',
      operation: 'delete',
      id,
    });
  },

  toggleActive: async (id: string) => {
    return await adminUpdate({
      table: 'chat_knowledge_base',
      operation: 'toggle-active',
      id,
    });
  },
};
